<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t, locale } = useI18n()
import {
  GetCurrentCartFromLocalStorage,
  GetCurrentCartIdentifiers,
  ShippingAddressAddToCart,
  BillingAddressAddToCart,
  CartLocalStorageKey,
  SaveCartToLocalStorage,
} from '../services/CartService'
import { MarketingOptInCreate,MarketingOptInGet } from '../api/Marketing.ts'
import {
  Form as kForm,
  FormElement as kformelement,
  type FormSubmitClickEvent
} from '@progress/kendo-vue-form'
import ApplicationField from '../application/ApplicationField.vue'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Popup } from '@progress/kendo-vue-popup'
import { Checkbox as KCheckBox } from '@progress/kendo-vue-inputs'
import {
  ExpansionPanel,
  ExpansionPanelContent,
} from '@progress/kendo-vue-layout'
import { FormatCurrency } from '../services/currencyFormatter'
import { GetCartProductSubtotal } from '../services/CartTotalsService'
import { Reveal } from '@progress/kendo-vue-animation'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '../logger.ts'
import { GetUniqueProductCodes, GuestAddToCart, CustomerAddToCart } from '../services/CartService'
import { ref, onMounted, provide, computed, watch } from 'vue'
import type { IAoeCart, IShippingAddress, ICartResponse } from '../types/CartTypes.js'
import type { IAddressVerificationListItem, IAddress } from '../types/CountryCultureTypes.js'
import type { IAoeCustomer } from '../types/CustomerTypes.js'
import {
  GetFullProductImageDictionary,
  GetFullProductUrl,
} from '../services/SkuService.ts'
import {
  GetCartDetailUrl,
  GetOrderTypeId,
  GetRootUrl,
  GetThankYouUrl,
  GetBeeNumberAuthenicatedUser,
  GetCountryCode,
  GetLoginUrl,
  GetCheckoutUrl,
  GetBeeNumberSite
} from '../stores/SettingsStore.ts'
import {
  trackBeginCheckout,
  trackAddPersonalInfo,
  trackAddShippingInfo,
  getFunnelType,
} from '../analytics/index'
import { buildMatomoItemsFromCart } from '../analytics/matomoItemBuilder'
import { createEmailFieldValidator } from '../validators/StringValidator.ts'
import { CartSubmit } from '../api/Cart.ts'
import AoeCheckoutPayment from './AoeCheckoutPayment.vue'
import { AlertCartSubmitResponse, handleError } from '../services/ErrorService.ts'
import VerifyAddressPopup from './VerifyAddressPopup.vue'
import { SetIsLoading } from '../services/AoeHelpers.ts'
import AddressControl from './PWS/AddressControl.vue'
import PhoneInputControl from './PhoneInputControl.vue'
import type { IPhoneOutput } from '../types/CommonTypes'
import { IntlProvider, LocalizationProvider } from '@progress/kendo-vue-intl'
import { getKendoLanguage, getKendoLocale } from '@/utils/kendoHelper'
import Swal from 'sweetalert2'


const logger = useLogger()

setStoreLogger(logger)

logger.debug('AoeCheckout.vue')

const cart = ref<IAoeCart | null>(null)
const cust = ref<IAoeCustomer | null>(null)
const productImageUrls = ref<Record<string, string>>({})
const orderSummaryItemTitle = ref('')
const addressVerifyError = ref(false)

// Computed property for login URL with return URL
const loginUrlWithReturnUrl = computed(() => {
      return  loginUrl.value + '?returnUrl=' + encodeURIComponent(returnUrl.value)
})

// const cartItems: ComputedRef<IAoeCartItem[]> = computed(
//   () => {
//     logger.debug('cartItemsWithoutDiscountIds')
//     if (!cart.value) return []

//     return cart.value.items
//   },
// )

const fullProductUrlByProductCode = ref<Record<string, string>>({})
const orderTypeId = ref<string>('')
const countryCode = ref('')
const rootUrl = ref<string>('')
const cartDetailUrl = ref<string>('')
const loginUrl = ref<string>('')
const expanded = ref(false)
const step = ref(1)
const show = ref(false)

const firstNameText = ref('')
const lastNameText = ref('')
const emailText = ref('')
const phoneNumberText = ref('')
const phoneModel = ref<IPhoneOutput>({
  number: '',
  countryCode: '',
  codeIdPhoneType: 'HOME',
  extension: null,
  formattedNumber: ''
})
const phoneControl = ref<InstanceType<typeof PhoneInputControl> | null>(null)

// Validation errors for standard inputs
const errors = ref({
  firstName: '',
  lastName: '',
  emailAddress: ''
})
const addressFirstNameText = ref('')
const addressLastNameText = ref('')
const addressLine1Text = ref('')
const addressLine2Text = ref<string | null>('')
const houseNumberText = ref<string | null>('')
const cityText = ref('')
const countryText = ref('')
const stateText = ref<string | null>('')
const postalCodeText = ref('')
const myForm = ref(null)
const mySecondForm = ref(null)
const verifyAddress = ref<IAddressVerificationListItem[]>([])
const isEditStep = ref(false)
const isAddressChange = ref(false)
const isBillingAddressChange = ref(false)
const shippingAddressControl = ref<InstanceType<typeof AddressControl> | null>(null)
const billingAddressControl = ref<InstanceType<typeof AddressControl> | null>(null)
const alreadyOptedin = ref(false)
const returnUrl = ref('')
const isUserLoggedIn = ref(false)
const sameAsBilling = ref(true)
const billingAddressFirstNameText = ref('')
const billingAddressLastNameText = ref('')
const billingAddressLine1Text = ref('')
const billingAddressLine2Text = ref<string | null>('')
const billingHouseNumberText = ref<string | null>('')
const billingCityText = ref('')
const billingCountryText = ref('')
const billingStateText = ref<string | null>('')
const billingPostalCodeText = ref('')

// Non-reactive display refs for AddressControl name fields to prevent space insertion bug
// See TEAU-969 fixes: d0783b0, 605ffb0
const shippingAddressNameDisplay = ref('')
const billingAddressNameDisplay = ref('')
const hasTrackedBeginCheckout = ref(false)
const isTrackingBeginCheckout = ref(false)
const hasTrackedAnyPersonalInfo = ref(false)
const hasTrackedAutofillShippingInfo = ref(false)
const lastTrackedPersonalInfoSnapshot = ref('')

// Computed property to check if cart has validation warnings that should block checkout
const hasBlockingValidationWarnings = computed(() => {
  return cart.value?.validationWarnings && cart.value.validationWarnings.length > 0
})

onMounted(async () => {
  logger.debug('onMounted')
  const urlParams = new URLSearchParams(window.location.search)
  const qsStep = urlParams.get('step')
  logger.debug('qsStep:', qsStep)
  if (qsStep) {
    step.value = parseInt(qsStep)
  }

  countryCode.value = await GetCountryCode()
  phoneModel.value.countryCode = countryCode.value
  orderTypeId.value = await GetOrderTypeId()
  rootUrl.value = await GetRootUrl()
  cartDetailUrl.value = await GetCartDetailUrl()
  loginUrl.value = await GetLoginUrl()
  returnUrl.value = await GetCheckoutUrl()

  const tempCart: IAoeCart | null = GetCurrentCartFromLocalStorage()
  if (tempCart) {
    const uniqueProductCodes = GetUniqueProductCodes(tempCart)
    logger.debug('uniqueProductCodes=', uniqueProductCodes)
    productImageUrls.value =
      await GetFullProductImageDictionary(uniqueProductCodes)
    logger.debug('productImageUrls', productImageUrls)
  }

  if (tempCart) await UpdateFullProductUrlDiscountedItems(tempCart)
  cart.value = tempCart

  //check if customer logged in, then check if cart has customer, if not add customer to cart
  const beeNumberAuthenicatedUser = await GetBeeNumberAuthenicatedUser()
  if (cart.value && beeNumberAuthenicatedUser) {
    if (cart.value.beeBusinessGuidCustomer == null || cart.value.beeBusinessGuidCustomer == undefined) {
      await CustomerAddToCart({
        aoeCartIdToken: cart.value.aoeCartIdToken,
        beeNumberCustomer: beeNumberAuthenicatedUser
      });
    }
  }


  await trackBeginCheckoutOnce(tempCart)

  if (beeNumberAuthenicatedUser) {
    cust.value = {
      emailAddress: cart.value?.customerEmailAddress,
      firstName: cart.value?.customerFirstName,
      lastName: cart.value?.customerLastName,
      phone: cart.value?.customerPhoneNumber,
    } as IAoeCustomer
    isUserLoggedIn.value = true
  }
  else
  {
    cust.value = {
      emailAddress: cart.value?.guestEmailAddress,
      firstName: cart.value?.guestFirstName,
      lastName: cart.value?.guestLastName,
      phone: cart.value?.guestPhoneNumber,
    } as IAoeCustomer

  }
  const response = await MarketingOptInGet({ emailAddress: cust.value.emailAddress })
  alreadyOptedin.value =  response.initialOptIn != null


  if (cust.value != null && cust.value.emailAddress != undefined)
    {
      formInitialValues.value = cust.value
      disableEmail.value = true

      firstNameText.value = cust.value.firstName
      lastNameText.value = cust.value.lastName
      emailText.value = cust.value.emailAddress
      phoneNumberText.value = cust.value.phone
      if (cust.value.phone) {
        phoneModel.value.number = cust.value.phone
        phoneModel.value.formattedNumber = cust.value.phone
      }

      await trackPersonalInfoAutofillOnce()
      moveToNextStep()
    }

  if (cart.value?.shippingAddress != null)
  {
    secondFormInitialValues.value = cart.value.shippingAddress
    addressFirstNameText.value = cart.value.shippingAddress.firstName
    addressLastNameText.value = cart.value.shippingAddress.lastName
    shippingAddressNameDisplay.value = (cart.value.shippingAddress.firstName || '') + ' ' + (cart.value.shippingAddress.lastName || '')
    addressLine1Text.value = cart.value.shippingAddress.addressLine1
    addressLine2Text.value = cart.value.shippingAddress.addressLine2
    houseNumberText.value = cart.value.shippingAddress.houseNumber
    cityText.value = cart.value.shippingAddress.city
    stateText.value = cart.value.shippingAddress.stateCode
    postalCodeText.value = cart.value.shippingAddress.postalCode
    countryText.value = cart.value.shippingAddress.countryCode

    await trackShippingInfoAutofillOnce()
  }

  if (cart.value?.billingAddress != null)
  {
    // Billing address exists, so uncheck "Same as billing" and populate billing fields
    sameAsBilling.value = false
    billingAddressFirstNameText.value = cart.value.billingAddress.firstName
    billingAddressLastNameText.value = cart.value.billingAddress.lastName
    billingAddressNameDisplay.value = (cart.value.billingAddress.firstName || '') + ' ' + (cart.value.billingAddress.lastName || '')
    billingAddressLine1Text.value = cart.value.billingAddress.addressLine1
    billingAddressLine2Text.value = cart.value.billingAddress.addressLine2 || ''
    billingHouseNumberText.value = cart.value.billingAddress.houseNumber || ''
    billingCityText.value = cart.value.billingAddress.city
    billingStateText.value = cart.value.billingAddress.stateCode || ''
    billingPostalCodeText.value = cart.value.billingAddress.postalCode
    billingCountryText.value = cart.value.billingAddress.countryCode
  }


  orderSummaryItemTitle.value = t('checkout.OrderSummaryYourOrder')
  if (cart.value) {
    let totalQty = 0
    for (const item of cart.value.items) {
      totalQty += item.quantity
    }

    orderSummaryItemTitle.value += '  (' + totalQty + ' ' + t('checkout.OrderSummaryItem') +')'
  }

  handleAction()
})

// Watch the show ref to lock/unlock body scrolling when popup opens/closes
watch(show, (newValue) => {
  if (newValue) {
    // Popup is opening - prevent body scroll
    document.body.style.overflow = 'hidden'
  } else {
    // Popup is closing - restore body scroll
    document.body.style.overflow = ''
  }
})

function handleAction() {
  expanded.value = !expanded.value
}

async function trackBeginCheckoutOnce(tempCart: IAoeCart | null) {
  if (!tempCart || tempCart.items.length === 0 || hasTrackedBeginCheckout.value || isTrackingBeginCheckout.value) {
    return
  }

  isTrackingBeginCheckout.value = true
  hasTrackedBeginCheckout.value = true

  try {
    const matomoItems = await buildMatomoItemsFromCart(tempCart, orderTypeId.value)
    const funnelType = getFunnelType(orderTypeId.value || tempCart.orderTypeId, tempCart.eventId)
    trackBeginCheckout(matomoItems, tempCart.total ?? 0, tempCart.currencyId || 'EUR', funnelType, tempCart.couponCode)
  } finally {
    isTrackingBeginCheckout.value = false
  }
}

async function trackPersonalInfoManual() {
  await trackPersonalInfo('manual')
}

async function trackShippingInfoManual() {
  await trackBeginCheckoutOnce(cart.value)
  if (!hasTrackedAnyPersonalInfo.value) {
    await trackPersonalInfoAutofillOnce()
  }
  const funnelType = getFunnelType(orderTypeId.value || cart.value?.orderTypeId, cart.value?.eventId)
  trackAddShippingInfo(
    cart.value?.total ?? 0,
    cart.value?.currencyId || 'EUR',
    funnelType
  )
}

async function trackShippingInfoAutofillOnce() {
  await trackBeginCheckoutOnce(cart.value)
  if (!hasTrackedAnyPersonalInfo.value) {
    await trackPersonalInfoAutofillOnce()
  }
  if (hasTrackedAutofillShippingInfo.value) {
    return
  }

  hasTrackedAutofillShippingInfo.value = true
  const funnelType = getFunnelType(orderTypeId.value || cart.value?.orderTypeId, cart.value?.eventId)
  trackAddShippingInfo(
    cart.value?.total ?? 0,
    cart.value?.currencyId || 'EUR',
    funnelType
  )
}

async function UpdateFullProductUrlDiscountedItems(cart: IAoeCart) {
  logger.debug('UpdateFullProductUrlDiscountedItems')
  for (const cartItem of cart.items.filter(
    item =>
      !item.poeDiscPlanLevelDiscIds ||
      item.poeDiscPlanLevelDiscIds.length === 0,
  )) {
    let fullProductUrl = fullProductUrlByProductCode.value[cartItem.productCode]
    if (fullProductUrl) continue
    fullProductUrl = await GetFullProductUrl(
      cartItem.categoryCode,
      cartItem.productCode,
    )
    fullProductUrlByProductCode.value[cartItem.productCode] = fullProductUrl
  }
}

function GetFullProductImageUrlFromStringDictionary(productCode: string) {
  logger.debug(
    'GetFullProductImageUrlFromStringDictionary',
    productCode,
    productImageUrls,
  )
  const productImageUrl = productImageUrls.value[productCode]
  logger.debug('productImageUrl=', productImageUrl)
  if (productImageUrl) return productImageUrl
  return '#'
}

function GetFullProductUrlFromStringDictionary(productCode: string): string {
  const fullProductUrl = fullProductUrlByProductCode.value[productCode]
  if (fullProductUrl) {
    logger.debug('productCode=', productCode, 'fullProductUrl=', fullProductUrl)
  } else {
    console.warn('productCode=', productCode, 'fullProductUrl=', fullProductUrl)
  }
  return fullProductUrl || '#'
}

window.addEventListener('storage', storageHandler, false)
async function storageHandler(event: StorageEvent) {
  logger.debug('storageHandler', event)
  const cartIdentifiers = GetCurrentCartIdentifiers()
  if (cartIdentifiers && event.key === CartLocalStorageKey) {
    cart.value = GetCurrentCartFromLocalStorage()

    if (cart.value?.customerFirstName != undefined)
    {
      cust.value = {
        emailAddress: cart.value?.customerEmailAddress,
        firstName: cart.value?.customerFirstName,
        lastName: cart.value?.customerLastName,
        phone: cart.value?.customerPhoneNumber,
      } as IAoeCustomer
    }
    else if (cart.value?.guestFirstName != undefined)
    {
      cust.value = {
        emailAddress: cart.value?.guestEmailAddress,
        firstName: cart.value?.guestFirstName,
        lastName: cart.value?.guestLastName,
        phone: cart.value?.guestPhoneNumber,
      } as IAoeCustomer
    }

    if (cust.value != null)
      {
        formInitialValues.value = cust.value
        disableEmail.value = true

        firstNameText.value = cust.value.firstName
        lastNameText.value = cust.value.lastName
        emailText.value = cust.value.emailAddress
        phoneNumberText.value = cust.value.phone
        if (cust.value.phone) {
          phoneModel.value.number = cust.value.phone
          phoneModel.value.formattedNumber = cust.value.phone
        }

        await trackPersonalInfoAutofillOnce()
      }

    if (cart.value?.shippingAddress != null && secondFormInitialValues.value.addressLine1 == undefined)
    {
      secondFormInitialValues.value = cart.value.shippingAddress
      addressFirstNameText.value = cart.value.shippingAddress.firstName
      addressLastNameText.value = cart.value.shippingAddress.lastName
      shippingAddressNameDisplay.value = (cart.value.shippingAddress.firstName || '') + ' ' + (cart.value.shippingAddress.lastName || '')
      addressLine1Text.value = cart.value.shippingAddress.addressLine1
      addressLine2Text.value = cart.value.shippingAddress.addressLine2
      houseNumberText.value = cart.value.shippingAddress.houseNumber
      cityText.value = cart.value.shippingAddress.city
      stateText.value = cart.value.shippingAddress.stateCode
      postalCodeText.value = cart.value.shippingAddress.postalCode
      countryText.value = cart.value.shippingAddress.countryCode

      await trackShippingInfoAutofillOnce()

      moveToNextStep()
    }
  }
}

const formInitialValues = ref<IAoeCustomer>({ } as IAoeCustomer)
const secondFormInitialValues = ref<IShippingAddress>({ } as IShippingAddress)
const disableEmail = ref(false)
const marketingOptIn = ref(false)
const hasTrackedAutofillPersonalInfo = ref(false)

function buildPersonalInfoSnapshot(): string {
  return JSON.stringify({
    firstName: firstNameText.value?.trim() ?? '',
    lastName: lastNameText.value?.trim() ?? '',
    emailAddress: emailText.value?.trim().toLowerCase() ?? '',
    phone: phoneNumberText.value?.trim() ?? '',
  })
}

async function trackPersonalInfo(source: 'manual' | 'autofill') {
  await trackBeginCheckoutOnce(cart.value)

  const currentSnapshot = buildPersonalInfoSnapshot()
  const isDuplicateSnapshot = currentSnapshot === lastTrackedPersonalInfoSnapshot.value

  if (source === 'autofill' && hasTrackedAutofillPersonalInfo.value) {
    return
  }

  if (isDuplicateSnapshot) {
    if (source === 'autofill') {
      hasTrackedAutofillPersonalInfo.value = true
    }
    return
  }

  const funnelType = getFunnelType(orderTypeId.value || cart.value?.orderTypeId, cart.value?.eventId)
  trackAddPersonalInfo(
    cart.value?.total ?? 0,
    cart.value?.currencyId || 'EUR',
    funnelType
  )
  hasTrackedAnyPersonalInfo.value = true
  lastTrackedPersonalInfoSnapshot.value = currentSnapshot

  if (source === 'autofill') {
    hasTrackedAutofillPersonalInfo.value = true
  }
}

async function trackPersonalInfoAutofillOnce() {
  await trackPersonalInfo('autofill')
}

async function handleMarketingOptIn(emailAddress: string) {
  try {
    if (marketingOptIn.value) {
      await MarketingOptInCreate({
        emailAddress: emailAddress,
        dataUsage: 'M'
      })
      logger.debug('Marketing opt-in created successfully')
    }
  } catch (error) {
    console.error('Error creating marketing opt-in:', error)
    await handleError(error, t)
  }
}

const formProperties = {
  mode: 'create',
} as ApplicationEditFormProperties

provide('ApplicationEditFormProperties', formProperties)

function moveToNextStep(){
  logger.debug('moveToNextStep')

  if (step.value == 1)
  {
    if (cart.value?.shippingAddress != null)
    {
      step.value = 3
    }
    else
    {
      step.value = 2
    }
  }
  else if (step.value == 2)
  {
    if (secondFormInitialValues.value != null)
    {
      step.value = 3
    }
  }
  // Once we reach the payment step (3), stay there. Calling moveToNextStep() again
  // (e.g. from the storage handler for returning customers whose info + shipping
  // address both pre-populate) must not push the user past payment, otherwise the
  // payment section collapses and only the "Edit" button is visible.

}

function editStep1ClickHandler() {
  logger.debug('editStep1ClickHandler')
  step.value = 1
  if (!isUserLoggedIn.value) {
    disableEmail.value = false
  }
}

function editStep2ClickHandler() {
  logger.debug('editStep2ClickHandler')
  step.value = 2
  isEditStep.value = true
}

async function onSubmitClickHandler(event: FormSubmitClickEvent) {
  logger.debug('event:', event)
  await performSubmit(event)
}

async function performSubmit(event: FormSubmitClickEvent) {
  logger.debug('event:', event)

  if (!isEditStep.value && (event.isModified == false && (event.event.submitter.id === 'btnContinue2' && !isAddressChange.value && !isBillingAddressChange.value) ) && formProperties.mode === 'create') {
    logger.debug('Form is not modified')
    // Still run validation so the user sees required field messages
    const shippingValid = shippingAddressControl.value?.validate() ?? true
    const billingValid = sameAsBilling.value ? true : (billingAddressControl.value?.validate() ?? true)
    if (!shippingValid || !billingValid) {
      return
    }
    return
  }
  else if (isEditStep.value && event.isModified == false && formProperties.mode === 'create' && event.event.submitter.id === 'btnContinue2' && !isAddressChange.value && !isBillingAddressChange.value) {
    logger.debug('Form is not modified, edit step')
    // Still run validation so the user sees required field messages
    const shippingValid = shippingAddressControl.value?.validate() ?? true
    const billingValid = sameAsBilling.value ? true : (billingAddressControl.value?.validate() ?? true)
    if (!shippingValid || !billingValid) {
      return
    }
    moveToNextStep();
    return
  }
  else if (event.isModified == false && formProperties.mode === 'update') {
    logger.debug('Form is not modified')
    return
  } else if (event.isValid === false) {
    logger.debug('Form is not valid')
    return
  } else {
    if (event.event.submitter.id === 'btnContinue1') {
      logger.debug('btnContinue1')
      
      // Validate phone control
      if (phoneControl.value && !phoneControl.value.validate()) {
        return
      }
      
      // Validate standard inputs
      validateFirstName()
      validateLastName()
      validateEmailAddress()
      if (errors.value.firstName || errors.value.lastName || errors.value.emailAddress) {
        return
      }
      
      phoneNumberText.value = phoneModel.value.formattedNumber || phoneModel.value.number


      await AddGuestToCart(emailText.value, firstNameText.value, lastNameText.value, phoneNumberText.value)
      disableEmail.value = true

      moveToNextStep()
      await trackPersonalInfoManual()
    } else if (event.event.submitter.id === 'btnContinue2') {
      logger.debug('btnContinue2')

      // addressFirstNameText.value = event.values.addressFirstName
      // addressLastNameText.value = event.values.addressLastName

      // if (addressFirstNameText.value == undefined && addressLastNameText.value == undefined)
      // {
      //   addressFirstNameText.value = firstNameText.value
      //   addressLastNameText.value = lastNameText.value
      // }

      // addressLine1Text.value = event.values.addressLine1
      // addressLine2Text.value = event.values.addressLine2
      // houseNumberText.value = event.values.houseNumber
      // cityText.value = event.values.city
      // stateText.value = event.values.stateCode
      // postalCodeText.value = event.values.postalCode

      // Validate shipping address fields
      const shippingValid = shippingAddressControl.value?.validate() ?? true
      // Validate billing address fields only when "same as billing" is unchecked
      const billingValid = sameAsBilling.value ? true : (billingAddressControl.value?.validate() ?? true)

      if (!shippingValid || !billingValid) {
        return
      }

      // Check if only billing address changed (no shipping address change)
      if (!isAddressChange.value && isBillingAddressChange.value && !sameAsBilling.value) {
        logger.debug('Only billing address changed, saving billing address')
        SetIsLoading(true)
        const cartToken = cart.value != null ? cart.value.aoeCartIdToken : ''
        console.log('Saving billing-only address with cart token:', cartToken)
        await addBillingAddressToCart(true, cartToken)
        SetIsLoading(false)
        moveToNextStep()
        await trackShippingInfoManual()
      } else {
        // Normal flow - shipping address changed or both changed
        addShippingAddressToCart(false)
      }
    }
  }
}

async function AddGuestToCart(emailAddress: string, firstName: string, lastName: string, phone: string)
{
  if (cart.value && (cart.value.beeNumberCustomer == null || cart.value.beeNumberCustomer == undefined || cart.value.beeNumberCustomer == 0)) {
    try {
      const guestCartData = {
        aoeCartIdToken: cart.value.aoeCartIdToken,
        emailAddress: emailAddress,
        firstName: firstName,
        lastName: lastName,
        phoneNumber: phone,
      };
      await GuestAddToCart(guestCartData);
    } catch (error) {
      console.error('Error in GuestAddToCart:', error);
      await handleError(error, t)
    }
  }
}

function handleValidationWarnings(cartResponse: IAoeCart | null) {
  if (!cartResponse?.validationWarnings || cartResponse.validationWarnings.length === 0) {
    return
  }

  logger.debug('Validation warnings found:', cartResponse.validationWarnings)
}

function useEnteredAddressClick() {
  logger.debug('useEnteredAddressClick')

  // Validate shipping address fields before proceeding
  const shippingValid = shippingAddressControl.value?.validate() ?? true
  if (!shippingValid) {
    SetIsLoading(false)
    return
  }

  SetIsLoading(true)
  addShippingAddressToCart(true)
}

function useSelectedAddressClick(selectedAddress: IAddress) {
  logger.debug('useSelectedAddressClick')
  SetIsLoading(true)
    if(selectedAddress != undefined)
    {
      verifyAddress.value = []
      addressLine1Text.value = selectedAddress.addressLine1
      addressLine2Text.value = selectedAddress.addressLine2
      houseNumberText.value = selectedAddress.houseNumber
      cityText.value = selectedAddress.city
      stateText.value = selectedAddress.stateCode
      postalCodeText.value = selectedAddress.postalCode
      countryText.value = selectedAddress.countryCode
      logger.debug('selectedAddress:', selectedAddress)

      addShippingAddressToCart(false)
    }
}

async function addShippingAddressToCart(skipAddressValidation: boolean)
{
  show.value = false;
  verifyAddress.value = []
  logger.debug('addShippingAddressToCart')
  const shippingAddress: IShippingAddress = {
        firstName: addressFirstNameText.value,
        lastName: addressLastNameText.value,
        addressLine1: addressLine1Text.value,
        addressLine2: addressLine2Text.value,
        houseNumber: houseNumberText.value,
        city: cityText.value,
        stateCode: stateText.value,
        postalCode: postalCodeText.value,
        countryCode: countryText.value,
      }

      //Call Cart Address Add
      logger.debug('ShippingAddressAddToCart')
      const cartToken = cart.value != null ? cart.value.aoeCartIdToken : ''
      console.log('Original cart token before shipping address:', cartToken)

      // const shippingResponse = await ShippingAddressAddToCart({
      //   aoeCartIdToken: cartToken,
      //   address: shippingAddress,
      //   skipAddressValidation: skipAddressValidation,
      // })

      // logger.debug('ShippingAddressAddToCart response:', shippingResponse)
      // for (const item of shippingResponse.items) {
      //   verifyAddress.value.push(item);
      // }

      // if (shippingResponse.item == null)
      // {
      //   addressVerifyError.value = true
      // }
      // else
      // {
      //   addressVerifyError.value = false
      //   // Update cart with the response from shipping address API if available
      //   if (shippingResponse.item) {
      //     cart.value = shippingResponse.item
      //   }
      // }

            //Call Cart Address Add
      logger.debug('ShippingAddressAddToCart')
      try {
        const response = await ShippingAddressAddToCart({
          aoeCartIdToken: cart.value != null ? cart.value.aoeCartIdToken : '',
          address: shippingAddress,
          skipAddressValidation: skipAddressValidation,
        })

        logger.debug('ShippingAddressAddToCart response:', response)
        for (const item of response.items) {
          verifyAddress.value.push(item);
        }

        // Update cart with response if available
        if (response.item != null) {
         // cart.value = response.item
          handleValidationWarnings(response.item)
        }

        if (response.item == null)
        {
          addressVerifyError.value = true
        }
        else
        {
          addressVerifyError.value = false
        }

        if (verifyAddress.value.length == 0 && addressVerifyError.value == false) {
          secondFormInitialValues.value = shippingAddress

          // If user entered a different billing address, submit it now using the same cart token
          if (!sameAsBilling.value && isBillingAddressChange.value) {
            console.log('Saving billing address with cart token:', cartToken)
            await addBillingAddressToCart(skipAddressValidation, cartToken)
          }

          moveToNextStep()
          await trackShippingInfoManual()
        }
        else
        {
          logger.debug('Setting show.value = true, verifyAddress.length:', verifyAddress.value.length, 'addressVerifyError:', addressVerifyError.value)
          show.value = true;
          logger.debug('show.value is now:', show.value)
        }
      } catch (error) {
        logger.error('ShippingAddressAddToCart error:', error)
        await handleError(error, t)
      } finally {
        SetIsLoading(false)
      }
}

function addressControlUpdateHandler(data: IAddress)
{
  logger.debug('addressControlUpdateHandler', data)
  addressFirstNameText.value = data.firstName != null ? data.firstName : ''
  addressLastNameText.value = data.lastName != null ? data.lastName : ''
  addressLine1Text.value = data.addressLine1
  addressLine2Text.value = data.addressLine2 != null ? data.addressLine2 : ''
  houseNumberText.value = data.houseNumber != null ? data.houseNumber : ''
  cityText.value = data.city
  stateText.value = data.stateCode
  postalCodeText.value = data.postalCode
  countryText.value = data.countryCode
  isAddressChange.value = true
}

function billingAddressControlUpdateHandler(data: IAddress)
{
  logger.debug('billingAddressControlUpdateHandler', data)
  billingAddressFirstNameText.value = data.firstName != null ? data.firstName : ''
  billingAddressLastNameText.value = data.lastName != null ? data.lastName : ''
  billingAddressLine1Text.value = data.addressLine1
  billingAddressLine2Text.value = data.addressLine2 != null ? data.addressLine2 : ''
  billingHouseNumberText.value = data.houseNumber != null ? data.houseNumber : ''
  billingCityText.value = data.city
  billingStateText.value = data.stateCode
  billingPostalCodeText.value = data.postalCode
  billingCountryText.value = data.countryCode
  isBillingAddressChange.value = true
}

async function addBillingAddressToCart(skipAddressValidation: boolean, cartToken: string)
{
  logger.debug('addBillingAddressToCart')
  console.log('Inside addBillingAddressToCart, cartToken:', cartToken)

  const billingAddress: IShippingAddress = {
        firstName: billingAddressFirstNameText.value,
        lastName: billingAddressLastNameText.value,
        addressLine1: billingAddressLine1Text.value,
        addressLine2: billingAddressLine2Text.value,
        houseNumber: billingHouseNumberText.value,
        city: billingCityText.value,
        stateCode: billingStateText.value,
        postalCode: billingPostalCodeText.value,
        countryCode: billingCountryText.value,
      }

      console.log('Token being passed to BillingAddressAddToCart:', cartToken)

      logger.debug('BillingAddressAddToCart')
      await BillingAddressAddToCart({
        aoeCartIdToken: cartToken,
        address: billingAddress,
        skipAddressValidation: skipAddressValidation,
      }).then(response => {
        logger.debug('BillingAddressAddToCart response:', response)
        console.log('BillingAddressAddToCart response:', response)
      }).catch(async error => {
        logger.error('BillingAddressAddToCart error:', error)
        console.error('BillingAddressAddToCart error:', error)
        await handleError(error, t)
      })
}

async function placeOrderClickHandler() {
  logger.debug('placeOrderClickHandler',cart.value)
  logger.debug('placeOrderClickHandler',marketingOptIn.value, cust.value?.emailAddress)
  if (cart.value) {
    if(marketingOptIn.value && (cust.value?.emailAddress || emailText.value))
    {
      await handleMarketingOptIn(cust.value?.emailAddress || emailText.value)
    }
    SetIsLoading(true)
    CartSubmit(cart.value?.aoeCartIdToken).then(response =>
    {
      logger.debug('response:', response)
      if (response.aoeOrderId)
      {
        GetThankYouUrl().then(url =>
        {
          window.location.href = url + "?aoeOrderIdToken=" + response.aoeOrderIdToken
        })
      }
      else
      {
        SetIsLoading(false)
        SaveCartToLocalStorage(response as ICartResponse)
        AlertCartSubmitResponse(t, response.item)
      }
    })
    .catch(async error => {
      SetIsLoading(false)
      await handleError(error, t)
    })
  }
}

// Validation functions for standard inputs
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return emailRegex.test(email)
}

const validateFirstName = () => {
  if (!firstNameText.value?.trim()) {
    errors.value.firstName = t('validators.requiredField', { fieldName: t('columns.firstName') })
  } else {
    const nameRegex = /^[\p{L}\s'-]+$/u
    if (!nameRegex.test(firstNameText.value)) {
      errors.value.firstName = t('validators.nameField', { fieldName: t('columns.firstName') })
    } else if (firstNameText.value.trim().length < 2) {
      errors.value.firstName = t('validators.fieldMinLength', { fieldName: t('columns.firstName'), minimumLength: 2 })
    } else if (firstNameText.value.length > 50) {
      errors.value.firstName = t('validators.fieldLength', { fieldName: t('columns.firstName'), maximumLength: 50 })
    } else {
      errors.value.firstName = ''
    }
  }
}

const validateLastName = () => {
  if (!lastNameText.value?.trim()) {
    errors.value.lastName = t('validators.requiredField', { fieldName: t('columns.lastName') })
  } else {
    const nameRegex = /^[\p{L}\s'-]+$/u
    if (!nameRegex.test(lastNameText.value)) {
      errors.value.lastName = t('validators.nameField', { fieldName: t('columns.lastName') })
    } else if (lastNameText.value.trim().length < 2) {
      errors.value.lastName = t('validators.fieldMinLength', { fieldName: t('columns.lastName'), minimumLength: 2 })
    } else if (lastNameText.value.length > 50) {
      errors.value.lastName = t('validators.fieldLength', { fieldName: t('columns.lastName'), maximumLength: 50 })
    } else {
      errors.value.lastName = ''
    }
  }
}

const validateEmailAddress = () => {
  if (!emailText.value?.trim()) {
    errors.value.emailAddress = t('validators.requiredField', { fieldName: t('columns.emailAddress') })
  } else if (!validateEmail(emailText.value)) {
    errors.value.emailAddress = t('validators.invalidEmail')
  } else {
    errors.value.emailAddress = ''
  }
}
// const addressLine1Validator = [createRequiredFieldValidator(t, 'addressLine1')]
// const cityValidator = [createRequiredFieldValidator(t, 'city')]
// const stateValidator = [createRequiredFieldValidator(t, 'stateCode')]
// const postalCodeValidator = [createRequiredFieldValidator(t, 'postalCode')]

</script>

<template>
  <div>

  <div class="aoe-checkout container" data-testid="checkout-container">
    <p class="pageHeader">{{ t("checkout.Checkout") }}</p>
    <p v-if="!isUserLoggedIn" class="pageSubHeader x">
      <a :href="loginUrlWithReturnUrl" class="login-link">{{ t("cart.SignIn") }}</a> {{ t("checkout.CheckoutSubHeaderRemainder") }}
    </p>

    <div class="row">
      <div class="col-12 col-md-8">
        <div id="BasicInfoDiv" class="checkOutSection" data-testid="basic-info-section">
          <kForm
            :initialValues="formInitialValues"
            :key="JSON.stringify(formInitialValues)"
            @submitclick="onSubmitClickHandler($event)"
            id="myForm"
            ref="myForm"
            data-testid="customer-info-form"
          >
            <kformelement>
              <div class="row">
                <div class="col-12 col-md-9 checkoutSectionHdr">{{ t("checkout.CheckoutYourInformationHdrStep") }}</div>
                <div v-if="step != 1" class="col-12 col-md-3 editBtn text-end">
                  <button id="step1Edit" @click="editStep1ClickHandler" data-testid="edit-step1-btn">
                    {{ t("checkout.CheckoutEditStep") }}
                  </button>
                </div>
              </div>
              <div v-if="step != 1" class="row mt-3">
                <div class="col-12">
                  {{ firstNameText }}&nbsp;{{ lastNameText }}
                </div>
                <div class="col-12">
                  {{ emailText }}
                </div>
                <div class="col-12">
                  {{ phoneNumberText }}
                </div>
                <div v-if="!alreadyOptedin && (cart?.beeNumberCustomer || step != 1)">
                    <KCheckBox
                        ref="recieveEmailsCheckBox"
                        :label="t('labels.EmailMoreInfoCheckbox')"
                        v-model="marketingOptIn"
                      ></KCheckBox></div>
              </div>
              <div v-if="step == 1" class="stepInfo stepOne">
                <fieldset class="k-form-fieldset">
                  <div class="row">
                    <div class="col-6">
                      <div class="form-field">
                        <label class="form-label required">{{ t('columns.firstName') }}</label>
                        <input
                          v-model="firstNameText"
                          type="text"
                          class="form-input"
                          :class="{ 'error': errors.firstName }"
                          required
                          @input="validateFirstName"
                          @blur="validateFirstName"
                        />
                        <div v-if="errors.firstName" class="error-message">{{ errors.firstName }}</div>
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="form-field">
                        <label class="form-label required">{{ t('columns.lastName') }}</label>
                        <input
                          v-model="lastNameText"
                          type="text"
                          class="form-input"
                          :class="{ 'error': errors.lastName }"
                          required
                          @input="validateLastName"
                          @blur="validateLastName"
                        />
                        <div v-if="errors.lastName" class="error-message">{{ errors.lastName }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-6">
                      <div class="form-field">
                        <label class="form-label required">{{ t('columns.emailAddress') }}</label>
                        <input
                          v-model="emailText"
                          type="email"
                          class="form-input"
                          :class="{ 'error': errors.emailAddress }"
                          :disabled="disableEmail"
                          required
                          @input="validateEmailAddress"
                          @blur="validateEmailAddress"
                        />
                        <div v-if="errors.emailAddress" class="error-message">{{ errors.emailAddress }}</div>
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="form-field">
                        <label class="form-label required">{{ t('columns.phone') }}</label>
                        <LocalizationProvider :language="getKendoLanguage(locale)">
                          <IntlProvider :locale="getKendoLocale(locale)">
                            <PhoneInputControl
                              ref="phoneControl"
                              v-model="phoneModel"
                              :required="true"
                              :showExtension="false"
                              :showPhoneType="false"
                              :placeholder="t('consultantCorner.contactDetails.PhonePlaceholder')"
                            />
                          </IntlProvider>
                        </LocalizationProvider>
                      </div>
                    </div>
                  </div>
                  <div class="row mt-2">
                    <div class="col-12 optin">
                      <KCheckBox
                        ref="recieveEmailsCheckBox"
                        :label="t('labels.EmailMoreInfoCheckbox')"
                        v-model="marketingOptIn"
                        data-testid="marketing-optin-checkbox-step1"
                      ></KCheckBox>
                    </div>
                  </div>
                  <div class="row mt-2">
                    <div class="col-12">
                      <KButton
                        ref="btnContinue1"
                        id="btnContinue1"
                        type="submit"
                        class="btnContinue"
                        :rounded="'large'"
                        :theme-color="'checkoutContinue'"
                        data-testid="continue-step1-btn"
                        >{{t('labels.CheckoutContinue')}}</KButton
                      >
                    </div>
                  </div>
                </fieldset>
              </div>
            </kformelement>
          </kForm>
        </div>
        <div id="ShippingInfoDiv" class="checkOutSection" data-testid="shipping-info-section">
          <kForm
            @submitclick="onSubmitClickHandler($event)"
            :key="JSON.stringify(secondFormInitialValues)"
            :initial-values="secondFormInitialValues"
            ref="mySecondForm"
            data-testid="shipping-form"
          >
            <kformelement>
              <div class="row">
                <div :class="!sameAsBilling && (step > 2 || cart?.billingAddress != null) ? 'col-12 col-md-6 checkoutSectionHdr' : 'col-12 col-md-9 checkoutSectionHdr'">
                  {{ t("checkout.CheckoutShippingHdrStep") }}
                </div>
                <div v-if="!sameAsBilling && (step > 2 || cart?.billingAddress != null)" class="col-12 col-md-3 checkoutSectionHdr">
                  {{ t('checkout.BillingAddressHeader') }}
                </div>
                <div v-if="step > 2" :class="!sameAsBilling && cart?.billingAddress != null ? 'col-12 col-md-3 editBtn text-end' : 'col-12 col-md-3 editBtn text-end'">
                  <button id="step2Edit" @click="editStep2ClickHandler" data-testid="edit-step2-btn">
                    {{ t("checkout.CheckoutEditStep") }}
                  </button>
                </div>
              </div>
              <div v-if="step > 2 || (step != 2 && cart?.shippingAddress != null)" class="row mt-3">
                <div :class="!sameAsBilling && (step > 2 || cart?.billingAddress != null) ? 'col-12 col-md-6' : 'col-12'">
                  <div v-if="countryCode == 'US' || countryCode == 'CA'">
                    <div class="col-12">
                      {{ addressFirstNameText }}&nbsp;{{ addressLastNameText }}
                    </div><div class="col-12">
                      {{ addressLine1Text }}
                    </div>
                    <div class="col-12">
                      {{ addressLine2Text }}
                    </div>
                    <div class="col-12">
                      {{ cityText }}<template v-if="stateText">,&nbsp;{{ stateText }}</template>&nbsp;{{ postalCodeText }}
                    </div>
                  </div>
                  <div v-if="countryCode == 'AT' || countryCode == 'DE' || countryCode == 'FR'">
                    <div class="col-12">
                      {{ addressFirstNameText }}&nbsp;{{ addressLastNameText }}
                    </div>
                    <div class="col-12">
                      {{ addressLine1Text }}
                    </div>
                    <div class="col-12">
                      {{ houseNumberText }}
                    </div>
                    <div v-if="countryCode != 'AT'" class="col-12">
                      {{ addressLine2Text }}
                    </div>
                    <div class="col-12">
                      {{ postalCodeText }}
                    </div>
                    <div class="col-12">
                      {{ cityText }}
                    </div>
                  </div>
                </div>
                <div v-if="!sameAsBilling && (step > 2 || cart?.billingAddress != null)" class="col-12 col-md-6">
                  <div v-if="billingCountryText == 'US' || billingCountryText == 'CA'">
                    <div class="col-12">
                      {{ billingAddressFirstNameText }}&nbsp;{{ billingAddressLastNameText }}
                    </div><div class="col-12">
                      {{ billingAddressLine1Text }}
                    </div>
                    <div class="col-12">
                      {{ billingAddressLine2Text }}
                    </div>
                    <div class="col-12">
                      {{ billingCityText }}<template v-if="billingStateText">,&nbsp;{{ billingStateText }}</template>&nbsp;{{ billingPostalCodeText }}
                    </div>
                  </div>
                  <div v-if="billingCountryText == 'AT' || billingCountryText == 'DE' || billingCountryText == 'FR'">
                    <div class="col-12">
                      {{ billingAddressFirstNameText }}&nbsp;{{ billingAddressLastNameText }}
                    </div>
                    <div class="col-12">
                      {{ billingAddressLine1Text }}
                    </div>
                    <div class="col-12">
                      {{ billingHouseNumberText }}
                    </div>
                    <div v-if="billingCountryText != 'AT'" class="col-12">
                      {{ billingAddressLine2Text }}
                    </div>
                    <div class="col-12">
                      {{ billingPostalCodeText }}
                    </div>
                    <div class="col-12">
                      {{ billingCityText }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="step == 2" class="mt-3 stepInfo stepTwo">
                <div class="col-12 checkoutSectionSubHdr">
                  {{ t("checkout.AddressRequirementMessage") }}
                </div>

                <fieldset class="k-form-fieldset">
                <AddressControl
                  ref="shippingAddressControl"
                  :countryCode="countryText"
                  :addressLine1="addressLine1Text"
                  :addressLine2="addressLine2Text"
                  :houseNumber="houseNumberText"
                  :city="cityText"
                  :stateCode="stateText"
                  :postalCode="postalCodeText"
                  :columnClass="'col-12'"
                  :showfullnamefield="true"
                  :hidecountrypicker="false"
                  :isdisabled="false"
                  :isrequired="true"
                  :name="shippingAddressNameDisplay"
                  @address-change="addressControlUpdateHandler"
                  data-testid="address-control">
                </AddressControl>
                  <div class="row mt-2">
                    <div class="col-12">
                      <KCheckBox
                        ref="sameAsBillingCheckBox"
                        v-model="sameAsBilling"
                        :label="t('labels.SameAsBillingCheckbox')"
                        data-testid="same-as-billing-checkbox"
                      ></KCheckBox>
                    </div>
                  </div>
                  <div v-if="!sameAsBilling" class="row mt-3">
                    <div class="col-12 checkoutSectionSubHdr">
                      {{ t('checkout.BillingAddressHeader') }}
                    </div>
                    <AddressControl
                      ref="billingAddressControl"
                      :countryCode="billingCountryText"
                      :addressLine1="billingAddressLine1Text"
                      :addressLine2="billingAddressLine2Text"
                      :houseNumber="billingHouseNumberText"
                      :city="billingCityText"
                      :stateCode="billingStateText"
                      :postalCode="billingPostalCodeText"
                      :columnClass="'col-12'"
                      :showfullnamefield="true"
                      :hidecountrypicker="false"
                      :isdisabled="false"
                      :isrequired="true"
                      :name="billingAddressNameDisplay"
                      @address-change="billingAddressControlUpdateHandler"
                      data-testid="billing-address-control">
                    </AddressControl>
                  </div>
                  <div class="row mt-2">
                    <div class="col-12">
                      <KButton
                        ref="btnContinue2"
                        id="btnContinue2"
                        class="btnContinue"
                        :rounded="'large'"
                        type="submit"
                        :theme-color="'checkoutContinue'"
                        data-testid="continue-step2-btn"
                        >{{t('labels.CheckoutContinue')}}</KButton
                      >
                    </div>
                  </div>
                </fieldset>
              </div>
            </kformelement>
          </kForm>
        </div>
        <div id="PaymentInfoDiv" class="checkOutSection" data-testid="payment-info-section">
          <div class="row">
            <div class="col-12 col-md-9 checkoutSectionHdr">{{t('checkout.CheckoutPaymentHdrStep')}}</div>
            <div v-if="step > 3" class="col-12 col-md-3 editBtn text-end">
              <button @click="step = 3" data-testid="edit-step3-btn">{{t('checkout.CheckoutEditStep')}}</button>
            </div>
          </div>
          <div v-if="step > 3" class="row">
            <div class="col-12">{{ t('labels.PaymentInfoPlaceholder') }}</div>
          </div>
          <div v-if="step == 3" class="stepInfo stepThree">
            <AoeCheckoutPayment
              v-if="cart != null"
              :cart="cart"
              data-testid="payment-component"
            ></AoeCheckoutPayment>

            <!-- Validation Warnings Section -->
            <div v-if="cart?.validationWarnings && cart.validationWarnings.length > 0" class="row mt-3">
              <div class="col-12">
                <div class="validation-warnings-container" data-testid="validation-warnings">
                  <div
                    v-for="(warning, index) in cart.validationWarnings"
                    :key="index"
                    class="validation-warning-item"
                  >
                    <span class="warning-icon">⚠️</span>
                    <span class="warning-text">{{ t(`cart.warnings.${warning.warningCode}`, warning.attributes || {}) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-5">
              <div class="col-12">
                <KButton
                  class="btnCheckout"
                  :rounded="'large'"
                  :theme-color="'primary'"
                  @click="placeOrderClickHandler"
                  :disabled="(cart?.balance != undefined && cart?.balance > 0) || hasBlockingValidationWarnings"
                  data-testid="place-order-btn"
                  >{{t('labels.CheckoutPlaceOrder')}}</KButton
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-4">
        <div class="checkOutSection checkOutSectionOrderSummary" data-testid="order-summary-section">
          <div class="row">
            <div class="col-12 checkoutSectionHdr">{{t('checkout.CheckoutOrderSummaryHdr')}}</div>
          </div>
          <div class="row">
            <div class="col-12"></div>
          </div>
          <ExpansionPanel
            :title="orderSummaryItemTitle"
            :expanded="expanded"
            :expand-icon="'arrow-seek-down'"
            :collapse-icon="'arrow-seek-up'"
            @action="handleAction"
            data-testid="order-summary-expansion-panel"
          >
            <Reveal :appear="expanded">
              <ExpansionPanelContent v-if="expanded">
                <div class="row">
                  <div class="col-12 checkoutItems">
                    <div
                      class="checkoutItems"
                      v-for="(cartItem, index) in cart?.items"
                      :key="cartItem.lineNumber"
                      :data-testid="`cart-item-${cartItem.lineNumber}`"
                    >
                      <div class="row mt-2 mobileCartItems">
                        <div class="product-image col-3">
                          <a
                            :href="
                              GetFullProductUrlFromStringDictionary(
                                cartItem.productCode,
                              )
                            "
                            :data-category-code="cartItem.productCode"
                            :data-testid="`product-link-${cartItem.productCode}`"
                          >
                            <img
                              :src="
                                GetFullProductImageUrlFromStringDictionary(
                                  cartItem.productCode,
                                )
                              "
                              class="img-fluid prodImage"
                              :alt="t('cart.ProductImageAlt')"
                              :data-testid="`product-image-${cartItem.productCode}`"
                            />
                          </a>
                        </div>
                        <div class="col-9 cartItemInfoMobile">
                          <div class="row">
                            <div class="col-10">
                              <div class="cartSkuName">
                                {{ cartItem.skuName }}
                              </div>
                              <div class="cartSkuCode">
                                #{{ cartItem.skuCode }}
                              </div>
                            </div>
                          </div>
                          <div class="row">
                            <div class="col-6">
                              <div class="number">
                                {{ t('checkout.Quantity') }}: {{ cartItem.quantity }}
                              </div>
                            </div>
                            <div class="col-6">
                              <div class="cartItemPrice" :data-testid="`cart-item-price-${cartItem.lineNumber}`">
                                {{ FormatCurrency((cartItem.price * cartItem.quantity) - (cartItem.totalDiscountAmount != null ? cartItem.totalDiscountAmount : 0), cart?.currencyId || null) }}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div  v-if="cart?.items.length != undefined ? index < cart?.items.length - 1 : false" class="row mt-2">
                          <div class="col-12">
                            <hr />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ExpansionPanelContent>
            </Reveal>
          </ExpansionPanel>

          <div class="row mt-3">
            <div class="col-8 lblTotalRegularPrice">{{t('checkout.CheckoutTotalRegularPrice')}}</div>
            <div class="col-4 lblTotalRegularPriceValue" data-testid="total-regular-price-value">
              {{ FormatCurrency(GetCartProductSubtotal(cart), cart?.currencyId || null) }}
            </div>
          </div>
          <div class="row">
            <div class="col-8 lblSubtotal">{{t('checkout.CheckoutSubtotal')}}</div>
            <div class="col-4 lblSubtotalValue" data-testid="subtotal-value">
              {{ FormatCurrency(cart?.subtotal || 0, cart?.currencyId || null) }}
            </div>
          </div>
          <div class="row">
            <div class="col-8 shipping-label">{{t('checkout.CheckoutShipping')}}</div>
            <div class="col-4 shipping-value" data-testid="shipping-value">
              {{ FormatCurrency(cart?.shippingCost || 0, cart?.currencyId || null) }}
            </div>
          </div>
          <div class="row">
            <div class="col-8 lblTotal">{{t('checkout.CheckoutTotal')}}</div>
            <div class="col-4 lblTotalValue" data-testid="total-value">
              {{ FormatCurrency(cart?.total || 0, cart?.currencyId || null) }}
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <KButton
                class="btnCheckout"
                :rounded="'large'"
                :theme-color="'primary'"
                @click="placeOrderClickHandler"
                :disabled="(cart?.balance != undefined && cart?.balance > 0) || hasBlockingValidationWarnings"
                data-testid="place-order-btn-summary"
                >{{t('labels.CheckoutPlaceOrder')}}</KButton
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-if="show" class="address-popup-backdrop" @click="show = false"></div>
  <Popup
    :show="show"
    popup-class='aoe-address-val-popup-content'
    position-mode="fixed"
    data-testid="address-verification-popup"
  >
    <VerifyAddressPopup
      :enteredAddress="{
        addressLine1: addressLine1Text,
        addressLine2: addressLine2Text,
        houseNumber: houseNumberText,
        city: cityText,
        stateCode: stateText,
        postalCode: postalCodeText,
        countryCode: countryText,
        firstName: addressFirstNameText,
        lastName: addressLastNameText,
      }"
      :verifyAddress="verifyAddress"
      :verifyAddressError="addressVerifyError"
      @use-entered-address="useEnteredAddressClick"
      @use-selected-address="useSelectedAddressClick"
      @re-enter-address="show = false"
      data-testid="verify-address-popup">
    </VerifyAddressPopup>
  </Popup>

  </div>
</template>

// Not scoped because these styles are shared with the Kit checkout component
<style lang="scss">
.aoe-checkout {

  .stepTwo .k-input-solid.k-invalid
  {
    border-color: var(--kendo-color-border, black) !important;
  }

  /* Backdrop overlay for address verification popup */
  .address-popup-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(black, 0.5);
      z-index: $z-modal-backdrop;
      overflow: hidden;
      cursor: pointer;
  }

  .aoe-address-val-popup-content {
      position: fixed;
      top: 50%;
      left: 50%;
      padding: $card-padding;
      transform: translate(-50%, -50%);
      max-width: 90vw;
      max-height: 80vh;
      overflow-y: auto;
      background: white;
      border-radius: $radius-md;
      box-shadow: 0 4px 20px rgba(black, 0.2);
      z-index: $z-critical;
      cursor: default;
  }

  @media (max-width: 768px) {
    .aoe-address-val-popup-content {
      max-width: 95vw;
      max-height: 90vh;
      padding: $spacing-4;
      top: 50%;
    }
  }

  input[type="radio"] {
      background-color: inherit;
      margin-right: 10px;
  }

  .divUseAddress
  {
    text-align: right;
  }

  .enteredAddress
  {
    margin-left: 30px;
  }

  .verifyaddressLine
  {
    margin-left: 30px;
  }

  .k-form .k-form-fieldset {
    margin-top: 10px;
  }

  .aoe-checkout {
    margin-top: 20px;
  }

  .login-link {
    @include font-weight-bold;
    color: #1a6e7d;
    text-decoration: none;
  }

  .login-link:hover {
    text-decoration: underline;
  }
  .pageSubHeader a{
    color: var(--vt-c-teal) !important
  }

  .editBtn {
    margin-top: 0.5rem;
  }

  .editBtn button {
    width: 100%;
    max-width: 100%;
    border: none;
    cursor: pointer;
    background-color: white;
    color: slategrey;
    @include typography-body-large;
  }

  @media (min-width: 768px) {
    .editBtn {
      margin-top: 0;
    }

    .editBtn button {
      width: auto;
    }
  }

  .checkOutSection {
      border: 1px solid var(--color-border);
      padding: $card-padding;
      border-radius: $radius-md;
      margin-top: $spacing-5;
  }

  .checkOutSection .k-floating-label-container {
      width: 100%;
  }

  .btnContinue {
      /* !important required: Override Kendo button defaults for custom checkout continue styling */
      color: white !important;
      background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
      display: flex;
      height: 48px;
      padding: $spacing-4 56px;
      justify-content: center;
      align-items: center;
      gap: $gap-sm;
      align-self: stretch;
      border-radius: $radius-md;
      border: none;
      text-align: center;
      @include typography-body;
      @include font-weight-semibold;
      font-style: normal;
      text-transform: uppercase;
      margin-top: $spacing-5;
  }

  .btnContinue:hover:not(:disabled) {
      background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
      filter: brightness(0.85);
  }

  .checkOutSectionOrderSummary .k-input {
      border: none !important;
  }

  .checkOutSectionOrderSummary .k-input-inner {
      text-align: right !important;
  }

  .checkOutSectionOrderSummary .k-expander {
      border-top: none;
      border-left: none;
      border-right: none;
  }

  .checkOutSectionOrderSummary .k-expander-title {
      color: var(--black);
  }

  .k-checkbox-wrap input[type="checkbox"] {
      width: 20px;
      height: 20px;
      border: 1px solid var(--color-border) !important;
  }

  .lblTotalRegularPriceValue,
  .lblSubtotalValue,
  .shipping-value,
  .lblTotalValue {
    text-align: right;
    @include font-weight-semibold;
  }

  .btnCheckout {
      width: 100%;
  }

  .validation-warnings-container {
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: $radius-md;
    padding: $spacing-4;
  }

  .validation-warning-item {
    display: flex;
    align-items: flex-start;
    gap: $spacing-2;
    padding: $spacing-2 0;

    &:not(:last-child) {
      border-bottom: 1px solid #ffe69c;
    }
  }

  .warning-icon {
    font-size: 1.25rem;
    line-height: 1;
    flex-shrink: 0;
  }

  // Custom form field styling to match PhoneInputControl
  .form-field {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: $spacing-3;
  }

  .form-label {
    @include typography-body;
    @include font-weight-medium;
    color: #333;
    margin-bottom: 0.5rem;
  }

  .form-label.required::after {
    content: ' *';
    color: #dc3545;
  }

  .form-input {
    padding: 0.75rem;
    @include typography-body;
    border: 1px solid #ced4da;
    border-radius: $radius-sm;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    height: 48px;
  }

  .form-input:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3);
    border-color: #007bff;
  }

  .form-input.error {
    border-color: #dc3545;
  }

  .form-input.error:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(#dc2626, 0.25);
  }

  .error-message {
    @include typography-small;
    color: #dc3545;
    margin-top: 0.25rem;
  }

  // Override PhoneInputControl to match custom form styling
  .phone-input-control .country-select {
    border-color: #ced4da;
    height: 48px;
  }

  .phone-input-control .country-select:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3);
  }

  .phone-input-control .k-textbox {
    border-color: #ced4da;
    height: 48px;
  }

  .phone-input-control .k-textbox:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3);
  }

  // Mobile responsive adjustments
  @media (max-width: 576px) {
    .stepOne .row {
      margin-bottom: $spacing-2;
    }

    .form-field {
      margin-bottom: $spacing-2;
    }
  }

  .warning-text {
    color: #856404;
    font-size: 0.9375rem;
    line-height: 1.5;
    font-weight: 500;
  }

}

</style>
